/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { IImageHostServiceRepository } from '../interfaces';

export class ImageHostServiceRepositoryImpl implements IImageHostServiceRepository {
    private mHeightScaleFactor: number = 1;

    private mImageHostBaseUrl: string = '';

    private mWidthScaleFactor: number = 1;

    private readonly DEFAULT_ATMOSPHERIC_IMAGE_HEIGHT: number = 540;

    private readonly DEFAULT_ATMOSPHERIC_IMAGE_WIDTH: number = 960;

    private readonly DEFAULT_EPISODE_BANNER_IMAGE_HEIGHT: number = 310;

    private readonly DEFAULT_EPISODE_BANNER_IMAGE_WIDTH: number = 554;

    private readonly DEFAULT_LEAGUE_SHOWCASE_BANNER_IMAGE_HEIGHT: number = 240;

    private readonly DEFAULT_LEAGUE_SHOWCASE_BANNER_IMAGE_WIDTH: number = 240;

    private readonly DEFAULT_MOVIE_POSTER_IMAGE_HEIGHT: number = 282;

    private readonly DEFAULT_MOVIE_POSTER_IMAGE_WIDTH: number = 188;

    private readonly DEFAULT_PARTNER_SOURCE_LOGO_IMAGE_HEIGHT: number = 32;

    private readonly DEFAULT_PARTNER_SOURCE_LOGO_IMAGE_WIDTH: number = 32;

    private readonly DEFAULT_PERSON_IMAGE_HEIGHT: number = 378;

    private readonly DEFAULT_PERSON_IMAGE_WIDTH: number = 284;

    private readonly DEFAULT_SCREEN_HEIGHT: number = 720;

    private readonly DEFAULT_SCREEN_WIDTH: number = 1280;

    private readonly DEFAULT_SHOWCASE_BANNER_IMAGE_HEIGHT: number = 282;

    private readonly DEFAULT_SHOWCASE_BANNER_IMAGE_WIDTH: number = 375;

    private readonly DEFAULT_TEAM_SHOWCASE_BANNER_IMAGE_HEIGHT: number = 240;

    private readonly DEFAULT_TEAM_SHOWCASE_BANNER_IMAGE_WIDTH: number = 240;

    private readonly DEFAULT_VOD_CONTENT_SHOWCASE_BANNER_IMAGE_HEIGHT: number = 310;

    private readonly DEFAULT_VOD_CONTENT_SHOWCASE_BANNER_IMAGE_WIDTH: number = 554;

    public constructor(baseUrl: string) {
        this.mImageHostBaseUrl = baseUrl;
        this.initializeScaleFactors();
    }

    /**
     * Initializes the scale factors for image resizing based on the current screen resolution.
     */
    private initializeScaleFactors(): void {
        const screenWidth =
            typeof window !== 'undefined' ? window.innerWidth : this.DEFAULT_SCREEN_WIDTH;
        const screenHeight =
            typeof window !== 'undefined' ? window.innerHeight : this.DEFAULT_SCREEN_HEIGHT;
        this.mWidthScaleFactor = screenWidth / this.DEFAULT_SCREEN_WIDTH;
        this.mHeightScaleFactor = screenHeight / this.DEFAULT_SCREEN_HEIGHT;
    }

    /**
     * Gets the scaled dimensions for an image based on the current screen resolution.
     * @param defaultWidth The default width of the image.
     * @param defaultHeight The default height of the image.
     * @returns The scaled dimensions for the image.
     */
    private getScaledDimensions(
        defaultWidth: number,
        defaultHeight: number,
    ): { width: number; height: number } {
        const scaledWidth = Math.floor(defaultWidth * this.mWidthScaleFactor);
        const scaledHeight = Math.floor(defaultHeight * this.mHeightScaleFactor);
        return { width: scaledWidth, height: scaledHeight };
    }

    /**
     * @inheritdoc
     */
    public getAtmosphericImageUrl(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_ATMOSPHERIC_IMAGE_WIDTH,
            this.DEFAULT_ATMOSPHERIC_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images?objectid=${objectId}&imagetype=atmospheric&width=${width}&height=${height}&extension=.png`;
    }

    /**
     * @inheritdoc
     */
    public getEpisodeBannerImageUrlForVodContent(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_EPISODE_BANNER_IMAGE_WIDTH,
            this.DEFAULT_EPISODE_BANNER_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images/${objectId}/episodeBanner_${width}x${height}?fallbackPolicy=episodeToSeries`;
    }

    /**
     * @inheritdoc
     */
    public getMoviePosterImageUrl(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_MOVIE_POSTER_IMAGE_WIDTH,
            this.DEFAULT_MOVIE_POSTER_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images?objectid=${objectId}&imagetype=moviePoster&width=${width}&height=${height}&fallbackPolicy=episodeToSeries`;
    }

    /**
     * @inheritdoc
     */
    public getPartnerSourceLogoImageUrl(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_PARTNER_SOURCE_LOGO_IMAGE_WIDTH,
            this.DEFAULT_PARTNER_SOURCE_LOGO_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images?objectid=${objectId}&imagetype=partnerSourceLogo&width=${width}&height=${height}`;
    }

    /**
     * @inheritdoc
     */
    public getPersonImageUrl(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_PERSON_IMAGE_WIDTH,
            this.DEFAULT_PERSON_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images?objectid=${objectId}&imagetype=person&width=${width}&height=${height}`;
    }

    /**
     * @inheritdoc
     */
    public getShowcaseBannerImageUrl(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_SHOWCASE_BANNER_IMAGE_WIDTH,
            this.DEFAULT_SHOWCASE_BANNER_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images?objectid=${objectId}&imagetype=showcaseBanner&width=${width}&height=${height}`;
    }

    /**
     * @inheritdoc
     */
    public getShowcaseBannerImageUrlForLeague(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_LEAGUE_SHOWCASE_BANNER_IMAGE_WIDTH,
            this.DEFAULT_LEAGUE_SHOWCASE_BANNER_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images?objectid=${objectId}&imagetype=showcaseBanner&width=${width}&height=${height}`;
    }

    /**
     * @inheritdoc
     */
    public getShowcaseBannerImageUrlForTeam(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_TEAM_SHOWCASE_BANNER_IMAGE_WIDTH,
            this.DEFAULT_TEAM_SHOWCASE_BANNER_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images?objectid=${objectId}&imagetype=showcaseBanner&width=${width}&height=${height}`;
    }

    /**
     * @inheritdoc
     */
    public getShowcaseBannerImageUrlForVodContent(objectId: string): string {
        const { width, height } = this.getScaledDimensions(
            this.DEFAULT_VOD_CONTENT_SHOWCASE_BANNER_IMAGE_WIDTH,
            this.DEFAULT_VOD_CONTENT_SHOWCASE_BANNER_IMAGE_HEIGHT,
        );
        return `${this.mImageHostBaseUrl}/images?objectid=${objectId}&imagetype=showcaseBanner&width=${width}&height=${height}`;
    }
}
